import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLoad } from '../../../../../../shared/hooks/load'
import { authGet } from '../../../../../../shared/sso-workspace/auth-api'
import { Pill } from '../../../../../../shared/sso-workspace/pill'
import { findingRule } from '../../../../../../shared/sso-workspace/finding-rules'
import { formatAgo, formatDateTime, plural } from '../../../../../../shared/sso-workspace/format'
import '../../../../../../shared/sso-workspace/fleet.scss'
import { FleetGrid, useGridList } from '../../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat } from '../../../../../../shared/table-builder/table-columns'
import { IconAuthLogin } from '../../../../../../shared/icon/icon'

export const RECENT_FINDINGS = 5

// Sign-in activity for one connection (audit friction: staff couldn't tell
// from the workspace whether anyone signs in through a connection, or what
// fails): figures for the last 7 and 30 days from GET
// /provider/:id/activity, the latest sign-ins, and the latest findings in
// plain words, with the way to all of them on the Findings page.
//
// The organization's own connection page reads its own people's activity
// (activityPath: org-sso/connections/:id/activity) and has no findings list
// or Findings page (showFindings false): the 30-day count tile stays.
export const SectionActivity = ({ providerID, activityPath, showFindings = true }) => {
  const [activity, setActivity] = useState(undefined)
  const [findings, setFindings] = useState(undefined)

  useLoad(async () => {
    const [a, f] = await Promise.allSettled([
      authGet(activityPath || `provider/${providerID}/activity`),
      showFindings ? authGet('auth-policy-finding', { providerID, limit: RECENT_FINDINGS }) : Promise.resolve([])
    ])
    setActivity(a.status === 'fulfilled' ? a.value || null : null)
    setFindings(f.status === 'fulfilled' ? f.value || [] : null)
  }, [providerID, activityPath, showFindings])

  const tableRef = useRef()
  // The latest sign-ins, as the grid's rows (undefined while loading). Each
  // gets an id: a sign-in has none of its own, and the grid keys rows on it.
  const recent = useMemo(
    () => (activity ? (activity.recentSignIns || []).map((s, i) => ({ ...s, id: `${s.at}-${i}` })) : activity),
    [activity]
  )
  const signIns = useGridList(recent)

  const figure = key => (activity === undefined ? '…' : activity === null ? 'Unavailable' : String(activity[key] ?? 0))
  const findings30 = activity ? activity.findings30d || 0 : 0

  return (
    <div className="d-grid gap-3">
      <div className="sw-strip m-0" role="region" aria-label="sign-in activity">
        <div className="sw-tile" data-testid="activity-last">
          <span className="sw-k">Last sign-in</span>
          <span className="sw-v">{activity === undefined ? '…' : activity === null ? 'Unavailable' : activity.lastSignInAt ? formatAgo(activity.lastSignInAt) : 'Never'}</span>
          {activity && activity.lastSignInAt && <span className="sw-d">{formatDateTime(activity.lastSignInAt)}</span>}
        </div>
        <div className="sw-tile" data-testid="activity-7d"><span className="sw-k">Sign-ins, 7 days</span><span className="sw-v">{figure('signIns7d')}</span></div>
        <div className="sw-tile" data-testid="activity-30d">
          <span className="sw-k">Sign-ins, 30 days</span><span className="sw-v">{figure('signIns30d')}</span>
          {activity && <span className="sw-d">{plural(activity.people30d || 0, 'person', 'people')}</span>}
        </div>
        <div className="sw-tile" data-testid="activity-findings">
          <span className="sw-k">Findings, 30 days</span>
          <span className={`sw-v ${findings30 ? 'warn' : ''}`.trim()}>{figure('findings30d')}</span>
          {activity && (activity.findingsByRule || []).length > 0 &&
            <span className="sw-d">{activity.findingsByRule.slice(0, 2).map(r => `${findingRule(r.rule).title.toLowerCase()} (${r.count})`).join(', ')}</span>}
        </div>
      </div>

      <div>
        <div className="sw-subh mb-2"><h3>Latest sign-ins</h3></div>
        {activity === null && <p className="sw-hint m-0">Sign-in activity couldn't be loaded.</p>}
        {activity !== null &&
          <FleetGrid tableRef={tableRef} exportName="sign-ins" compact flush title="latest sign-ins">
            <TableAgClient
              ref={tableRef}
              queryKey="signins"
              apiList={signIns}
              sortCol="at"
              sortDesc
              emptyState={{ Icon: IconAuthLogin, title: 'No sign-ins yet', message: "Nobody has signed in through this connection yet. Once a domain is bound and it's active, sign-ins show here." }}>
              <ColumnFormat field="at" header="When" render={whenCell} sortable />
              <ColumnFormat field="email" header="Person" render={unknownCell} sortable />
              <ColumnFormat field="organizationName" header="Organization" render={noneCell} sortable />
              <ColumnFormat field="deviceType" header="Device" render={unknownCell} sortable />
            </TableAgClient>
          </FleetGrid>}
      </div>

      {showFindings &&
      <div>
        <div className="sw-subh mb-2">
          <h3>Latest findings</h3>
          <Link className="btn btn-sm sw-btn" to={`/admin/sso/findings?providerID=${providerID}`}>All findings</Link>
        </div>
        {findings === null && <p className="sw-hint m-0">Findings couldn't be loaded.</p>}
        {Array.isArray(findings) && findings.length === 0 && <p className="sw-hint m-0">No findings. Nothing about this connection has been blocked or reported.</p>}
        {Array.isArray(findings) && findings.length > 0 &&
          <ul className="sw-notes">
            {findings.map(f =>
              <li key={f.id} data-tone={f.enforced ? 'warn' : undefined}>
                <span>
                  <span className="fw-semibold">{findingRule(f.rule).title}</span>
                  {f.email && <> for {f.email}</>}
                  <span className="sw-hint"> · {formatAgo(f.createdAt)}</span>
                  {f.enforced && <> <Pill tone="bad">Blocked</Pill></>}
                  {findingRule(f.rule).fix && <span className="sw-cell-sub">{findingRule(f.rule).fix}</span>}
                </span>
              </li>)}
          </ul>}
      </div>}
    </div>
  )
}

const whenCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')
const unknownCell = row => row.value || <span className="sw-hint">Unknown</span>
const noneCell = row => row.value || <span className="sw-hint">None</span>
